import React, { FC } from 'react'
import { useSearchAnalytics } from '../../../../pages/SearchPage/analytics'
import { navigatePagesEvent } from '../../../../pages/SearchPage/events'
import { useOptionalSearchStore } from '../../../../store/SearchStore'
import { Flex, PrimaryButton, Text } from '@moonpig/launchpad-components'
import { useFindLocaleText } from '../../../../text-localisation'
import {
  IconSystemChevronLeft,
  IconSystemChevronRight,
} from '@moonpig/launchpad-assets'
import { styled } from '@moonpig/launchpad-utils'
import { NAV_BUTTON_SIZE_MOBILE } from '../constants'
import { system as s } from '@moonpig/launchpad-system'

const ICON_SIZE = '24'

const StyledPrimaryButton = styled(PrimaryButton)`
  text-decoration: none;
  ${s({
    width: { xs: NAV_BUTTON_SIZE_MOBILE, md: 'auto' },
    height: { xs: NAV_BUTTON_SIZE_MOBILE, md: 'auto' },
    minHeight: { xs: NAV_BUTTON_SIZE_MOBILE, md: 'auto' },
  })}
  
  &:disabled {
    pointer-events: none;
    ${s({
      visibility: 'hidden',
    })}
  }

  span {
    ${s({
      fontWeight: '400',
    })}
  }
`

type NavControlProps = {
  disabled: boolean
  currentPage: number
  onClick: (event: React.MouseEvent<HTMLElement>, page: number) => void
  getPageHref: (page: number) => string
}

export const PreviousButton: FC<NavControlProps> = ({
  disabled,
  currentPage,
  onClick,
  getPageHref,
}) => {
  const isLaunchpadAnalytics = useSearchAnalytics()

  const pageType = useOptionalSearchStore(
    store => store.pageContext.type,
    'search',
  )

  const localiseText = useFindLocaleText()

  return (
    <StyledPrimaryButton
      href={!disabled ? getPageHref(currentPage - 1) : undefined}
      disabled={disabled}
      onClick={event => onClick(event, currentPage - 1)}
      events={
        isLaunchpadAnalytics
          ? {
              // Legacy event: apps/explore/find/src/store/middleware/trackEvent.ts
              onClick: navigatePagesEvent()
                .withEventData({
                  category: `${pageType} action`.toLowerCase(),
                  label: 'previous page',
                })
                .withContentData({
                  content_type:
                    `${pageType} action | navigate pages | previous page`.toLowerCase(),
                }),
            }
          : undefined
      }
      aria-label={localiseText('find.pagination_go_to_previous_page')}
      aria-hidden={disabled}
    >
      <Flex alignItems="center">
        <Flex aria-hidden>
          <IconSystemChevronLeft width={ICON_SIZE} height={ICON_SIZE} />
        </Flex>
        <Text display={{ xs: 'none', md: 'block' }}>
          {localiseText('find.pagination_previous')}
        </Text>
      </Flex>
    </StyledPrimaryButton>
  )
}

export const NextButton: FC<NavControlProps> = ({
  disabled,
  currentPage,
  onClick,
  getPageHref,
}) => {
  const isLaunchpadAnalytics = useSearchAnalytics()

  const pageType = useOptionalSearchStore(
    store => store.pageContext.type,
    'search',
  )

  const localiseText = useFindLocaleText()

  return (
    <StyledPrimaryButton
      href={!disabled ? getPageHref(currentPage + 1) : undefined}
      disabled={disabled}
      onClick={event => onClick(event, currentPage + 1)}
      events={
        isLaunchpadAnalytics
          ? {
              // Legacy event: apps/explore/find/src/store/middleware/trackEvent.ts
              onClick: navigatePagesEvent()
                .withEventData({
                  category: `${pageType} action`.toLowerCase(),
                  label: 'next page',
                })
                .withContentData({
                  content_type:
                    `${pageType} action | navigate pages | next page`.toLowerCase(),
                }),
            }
          : undefined
      }
      aria-label={localiseText('find.pagination_go_to_next_page')}
      aria-hidden={disabled}
    >
      <Flex alignItems="center">
        <Text display={{ xs: 'none', md: 'block' }}>
          {localiseText('find.pagination_next')}
        </Text>
        <Flex aria-hidden>
          <IconSystemChevronRight width={ICON_SIZE} height={ICON_SIZE} />
        </Flex>
      </Flex>
    </StyledPrimaryButton>
  )
}
